<script>
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import dragDrop from 'drag-drop';
import { SessionAnalytics } from '@/services/analytics';
import { parsedTorrentFailedMessage, parseTorrents } from '@/utils/torrent';
import {
    TermsOfServicePopup,
    HelpImproveBanner,
    DragDropOverlay,
    Footer,
    Header,
    PlaybackInterruptModal,
} from '@/components/shared';

export default {
    name: 'App',
    components: {
        HelpImproveBanner,
        Header,
        Footer,
        DragDropOverlay,
        TermsOfServicePopup,
        PlaybackInterruptModal,
    },
    data() {
        return {
            shownNotification: false,
            isProccessing: false,
            dragDrop: false,
        };
    },
    computed: {
        ...mapGetters('product', ['askForConsentResolver', 'askForInterruptResolver']),
    },
    created() {
        this.$Logger.debug('lifecycle', { event: 'created', component: 'App' });
        SessionAnalytics.initialize();
        window.addEventListener('beforeunload', () => SessionAnalytics.submit());
    },
    mounted() {
        this.$Logger.debug('lifecycle', { event: 'mounted', component: 'App' });
        dragDrop('#app', {
            onDrop: (files) => {
                if (this.isProccessing) {
                    return;
                }
                if (files.length !== 1) {
                    this.$toast?.error(`Please upload only a single '.torrent' file, ${files.length} were uploaded!`);
                    return;
                }
                this.isProccessing = true;
                parseTorrents(files[0])
                    .then((magnetLink) => {
                        this.$router
                            .push({
                                path: `/player`,
                                query: {
                                    m: btoa(magnetLink),
                                },
                            })
                            .then((failure) => {
                                if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
                                    this.$toast.warn('Torrent already loaded!');
                                    return;
                                }
                                SessionAnalytics.dragNDrop();
                            });
                    })
                    .catch((err) => {
                        this.$toast?.error(parsedTorrentFailedMessage(err.message));
                        console.error(err);
                        SessionAnalytics.dragNDrop(false);
                    })
                    .finally((_) => {
                        this.isProccessing = false;
                    });
            },
            onDragEnter: () => {
                this.dragDrop = true;
                document.body.style.overflow = 'hidden';
            },
            onDragLeave: () => {
                this.dragDrop = false;
                document.body.style.overflow = 'auto';
            },
        });

        window.addEventListener('resize', this.resize);
        window.addEventListener('keyup', this.keyup);
        window.addEventListener('keydown', this.keydown);
    },
    unmounted() {
        window.removeEventListener('resize', this.resize);
        window.removeEventListener('keyup', this.keyup);
        window.removeEventListener('keydown', this.keydown);
    },
    methods: {
        ...mapMutations('product', ['setReplaceModal']),
        ...mapActions('product', ['keydown', 'keyup', 'resize']),
    },
};
</script>

<template>
    <div id="app" class="application">
        <TermsOfServicePopup v-if="askForConsentResolver !== null" @update="askForConsentResolver()" />
        <HelpImproveBanner />
        <Header />
        <div class="main">
            <router-view></router-view>
        </div>
        <DragDropOverlay :style="{ display: dragDrop ? 'block' : 'none' }" />
        <PlaybackInterruptModal
            v-if="askForInterruptResolver !== null"
            @update="(resp) => askForInterruptResolver(resp)"
        />
        <Footer />
    </div>
</template>
`
<style lang="scss">
@import url('./fonts/Inter/inter.css');

.application {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .main {
        flex: 1;
    }

    a {
        text-decoration: none;
        color: inherit;
    }

    &.drag {
        #drag-drop-container {
            display: flex;
        }
    }

    #nav {
        text-align: center;
        margin: 30px 0 0 0;
    }
}
</style>
